/**
 * Panel lateral deslizable del AI Copilot.
 *
 * Usa estilos inline (no Tailwind) porque se renderiza fuera del Frame
 * de Polaris y necesita position:fixed real contra el viewport.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAICopilot } from '@/contexts/AICopilotContext';
import { AI_QUICK_ACTIONS } from '@/types/ai.types';
import type { AICopilotAction, AICopilotMessage } from '@/types/ai.types';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@smarty/shared';

// ─── Typewriter hook ────────────────────────────────────────────────────────

/**
 * Reveals `target` text character-by-character at ~charsPerFrame chars per
 * animation frame, creating a smooth ChatGPT-like typewriter effect.
 */
function useTypewriter(target: string | null, charsPerFrame = 3): string {
  const [displayed, setDisplayed] = useState('');
  const posRef = useRef(0);
  const targetRef = useRef('');
  const rafRef = useRef(0);

  useEffect(() => {
    if (target === null) {
      // Stream ended — show nothing (final message is in state.messages)
      setDisplayed('');
      posRef.current = 0;
      targetRef.current = '';
      cancelAnimationFrame(rafRef.current);
      return;
    }

    targetRef.current = target;

    // If we've already caught up, nothing to do
    if (posRef.current >= target.length) return;

    const tick = () => {
      const t = targetRef.current;
      const next = Math.min(posRef.current + charsPerFrame, t.length);
      posRef.current = next;
      setDisplayed(t.slice(0, next));

      if (next < t.length) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    // Only start the loop if it's not already running
    if (!rafRef.current || posRef.current >= displayed.length) {
      rafRef.current = requestAnimationFrame(tick);
    }

    return () => { /* don't cancel — let it keep ticking */ };
  }, [target, charsPerFrame, displayed.length]);

  // Cleanup on unmount
  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  return target === null ? '' : displayed;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const PANEL_WIDTH = 400;

const styles = {
  backdrop: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(0,0,0,0.2)',
    zIndex: 99998,
    transition: 'opacity 0.3s',
  },
  panel: (open: boolean) => ({
    position: 'fixed' as const,
    top: 0,
    right: 0,
    height: '100vh',
    width: PANEL_WIDTH,
    maxWidth: '90vw',
    background: '#fff',
    boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
    zIndex: 99999,
    display: 'flex',
    flexDirection: 'column' as const,
    transform: open ? 'translateX(0)' : `translateX(${PANEL_WIDTH}px)`,
    transition: 'transform 0.3s ease-in-out',
  }),
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: '1px solid #E5E7EB',
    background: 'linear-gradient(135deg, #2563EB, #4F46E5)',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#fff',
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  headerSubtitle: {
    fontSize: 11,
    color: '#BFDBFE',
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  headerBtn: {
    padding: 6,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#BFDBFE',
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  messagesArea: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '12px 16px',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '32px 0',
  },
  emptyIcon: { fontSize: 32, marginBottom: 8 },
  emptyTitle: { fontSize: 14, fontWeight: 500, color: '#374151', margin: '0 0 4px' },
  emptySubtitle: { fontSize: 12, color: '#6B7280', margin: '0 0 24px' },
  quickActionsWrap: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 8,
    justifyContent: 'center',
  },
  quickChip: (disabled: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: 20,
    border: '1px solid #E5E7EB',
    background: '#fff',
    fontSize: 12,
    fontWeight: 500,
    color: '#374151',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
  }),
  bubble: (isUser: boolean) => ({
    display: 'flex',
    justifyContent: isUser ? 'flex-end' : 'flex-start',
    marginBottom: 12,
  }),
  bubbleContent: (isUser: boolean) => ({
    maxWidth: '85%',
    borderRadius: 12,
    padding: '8px 12px',
    fontSize: 13,
    lineHeight: '1.6',
    wordBreak: 'break-word' as const,
    ...(isUser
      ? { background: '#2563EB', color: '#fff', borderBottomRightRadius: 4 }
      : { background: '#F3F4F6', color: '#111827', borderBottomLeftRadius: 4 }),
  }),
  loadingDots: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: 12,
  },
  loadingBubble: {
    background: '#F3F4F6',
    borderRadius: 12,
    padding: '12px 16px',
    borderBottomLeftRadius: 4,
    display: 'flex',
    gap: 4,
  },
  dot: (delay: number) => ({
    width: 8,
    height: 8,
    background: '#9CA3AF',
    borderRadius: '50%',
    animation: `aicopilot-bounce 1s ${delay}ms infinite`,
  }),
  error: {
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: 8,
    padding: '8px 12px',
    marginBottom: 12,
    fontSize: 12,
    color: '#B91C1C',
  },
  quickBarWrap: {
    padding: '8px 16px',
    borderTop: '1px solid #F3F4F6',
    overflowX: 'auto' as const,
  },
  quickBarInner: {
    display: 'flex',
    gap: 6,
    minWidth: 'max-content',
  },
  inputArea: {
    padding: '12px 16px',
    borderTop: '1px solid #E5E7EB',
    background: '#F9FAFB',
  },
  inputRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 8,
  },
  textarea: {
    flex: 1,
    resize: 'none' as const,
    borderRadius: 8,
    border: '1px solid #D1D5DB',
    padding: '8px 12px',
    fontSize: 13,
    outline: 'none',
    fontFamily: 'inherit',
    maxHeight: 120,
  },
  sendBtn: (disabled: boolean) => ({
    flexShrink: 0,
    width: 36,
    height: 36,
    borderRadius: 8,
    border: 'none',
    background: disabled ? '#9CA3AF' : '#2563EB',
    color: '#fff',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  disclaimer: {
    fontSize: 10,
    color: '#9CA3AF',
    marginTop: 6,
    textAlign: 'center' as const,
  },
  feedbackRow: {
    display: 'flex',
    gap: 2,
    marginTop: 4,
    marginLeft: 2,
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  feedbackBtn: (active: boolean, positive: boolean, voted: boolean) => ({
    background: active ? (positive ? '#DCFCE7' : '#FEE2E2') : 'transparent',
    border: '1px solid',
    borderColor: active ? (positive ? '#86EFAC' : '#FCA5A5') : 'transparent',
    cursor: voted ? 'default' : 'pointer',
    padding: '3px 6px',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: active ? (positive ? '#16A34A' : '#DC2626') : '#9CA3AF',
    transition: 'all 0.2s ease',
    transform: active ? 'scale(1.05)' : 'scale(1)',
    opacity: voted && !active ? 0.35 : 1,
  }),
};

// ─── Markdown renderer ───────────────────────────────────────────────────────
// Handles: **bold**, *italic*, `code`, bullet lists (- / •), numbered lists, headers (##)

function renderMarkdown(text: string): React.ReactNode {
  const lines = text.split('\n');
  const result: React.ReactNode[] = [];
  let key = 0;

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];

    // Skip empty lines — add spacing
    if (raw.trim() === '') {
      result.push(<div key={key++} style={{ height: 6 }} />);
      continue;
    }

    // ## Heading
    if (/^#{1,3}\s/.test(raw)) {
      const content = raw.replace(/^#{1,3}\s*/, '');
      result.push(
        <div key={key++} style={{ fontWeight: 700, fontSize: 13, marginTop: 8, marginBottom: 2, color: '#111827' }}>
          {renderInline(content)}
        </div>
      );
      continue;
    }

    // Bullet list item: - text or • text
    if (/^[-•]\s/.test(raw.trim())) {
      const content = raw.trim().replace(/^[-•]\s*/, '');
      result.push(
        <div key={key++} style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
          <span style={{ color: '#6B7280', flexShrink: 0, marginTop: 1 }}>•</span>
          <span>{renderInline(content)}</span>
        </div>
      );
      continue;
    }

    // Numbered list item: 1. text
    if (/^\d+\.\s/.test(raw.trim())) {
      const num = raw.trim().match(/^(\d+)\.\s/)?.[1];
      const content = raw.trim().replace(/^\d+\.\s*/, '');
      result.push(
        <div key={key++} style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
          <span style={{ color: '#6B7280', flexShrink: 0, minWidth: 16, marginTop: 1 }}>{num}.</span>
          <span>{renderInline(content)}</span>
        </div>
      );
      continue;
    }

    // Normal paragraph
    result.push(
      <div key={key++} style={{ marginBottom: 2 }}>
        {renderInline(raw)}
      </div>
    );
  }

  return result;
}

/** Render inline markdown: **bold**, *italic*, `code` */
function renderInline(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  // Split on **bold**, *italic*, `code`
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let k = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[2] !== undefined) {
      parts.push(<strong key={k++} style={{ fontWeight: 700 }}>{match[2]}</strong>);
    } else if (match[3] !== undefined) {
      parts.push(<em key={k++}>{match[3]}</em>);
    } else if (match[4] !== undefined) {
      parts.push(
        <code key={k++} style={{ background: '#E5E7EB', borderRadius: 3, padding: '1px 4px', fontSize: 12, fontFamily: 'monospace' }}>
          {match[4]}
        </code>
      );
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length === 1 ? parts[0] : parts;
}

// ─── Keyframes injection (once) ──────────────────────────────────────────────

let keyframesInjected = false;
function injectKeyframes() {
  if (keyframesInjected) return;
  keyframesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes aicopilot-bounce {
      0%, 80%, 100% { transform: translateY(0); }
      40% { transform: translateY(-6px); }
    }
  `;
  document.head.appendChild(style);
}

// ─── Main panel ──────────────────────────────────────────────────────────────

export function AICopilotPanel() {
  const { state, close, send, quickAction, clearThread } = useAICopilot();
  const { tenantId } = useAuth();
  const [input, setInput] = useState('');
  const [feedbackMap, setFeedbackMap] = useState<Map<number, 1 | -1>>(new Map());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const typewriterText = useTypewriter(state.streamingText);

  // Reset feedback when thread is cleared
  useEffect(() => {
    setFeedbackMap(new Map());
  }, [state.threadId]);

  useEffect(() => { injectKeyframes(); }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [state.messages, state.loading, typewriterText]);

  useEffect(() => {
    if (state.open) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [state.open]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || state.loading) return;
    setInput('');
    await send(text);
  }, [input, state.loading, send]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const handleQuickAction = useCallback(async (action: AICopilotAction) => {
    if (state.loading) return;
    await quickAction(action);
  }, [state.loading, quickAction]);

  const handleFeedback = useCallback(async (messageIndex: number, rating: 1 | -1) => {
    if (feedbackMap.has(messageIndex)) return; // ya votó
    setFeedbackMap(prev => new Map(prev).set(messageIndex, rating));
    const msg = state.messages[messageIndex];
    if (!msg || !tenantId) return;
    await supabase.from('ai_copilot_feedback').insert({
      tenant_id: tenantId,
      thread_id: state.threadId,
      message_content: msg.content.slice(0, 2000),
      rating,
      context_type: state.contextType !== 'general' ? state.contextType : null,
      context_id: state.contextId,
    });
  }, [feedbackMap, state.messages, state.threadId, state.contextType, state.contextId, tenantId]);

  const quickActions = AI_QUICK_ACTIONS[state.contextType] ?? AI_QUICK_ACTIONS.general;
  const hasMessages = state.messages.length > 0;

  return (
    <>
      {/* Backdrop */}
      {state.open && (
        <div style={styles.backdrop} onClick={close} aria-hidden />
      )}

      {/* Panel */}
      <div style={styles.panel(state.open)}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <span style={{ fontSize: 18 }}>✨</span>
            <div style={{ minWidth: 0 }}>
              <p style={styles.headerTitle}>AI Copilot</p>
              {state.contextLabel && (
                <p style={styles.headerSubtitle}>{state.contextLabel}</p>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {hasMessages && (
              <button
                type="button"
                onClick={clearThread}
                style={styles.headerBtn}
                title="Nueva conversación"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M1 7h12M7 1v12" />
                </svg>
              </button>
            )}
            <button type="button" onClick={close} style={styles.headerBtn}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M4 12L12 4M12 12L4 4" />
              </svg>
            </button>
          </div>
        </div>

        {/* Messages area */}
        <div style={styles.messagesArea}>
          {!hasMessages && !state.loading && (
            <div style={styles.emptyState}>
              <p style={styles.emptyIcon}>✨</p>
              <p style={styles.emptyTitle}>
                {state.contextLabel ? `AI Copilot — ${state.contextLabel}` : 'AI Copilot'}
              </p>
              <p style={styles.emptySubtitle}>
                Pregúntame lo que quieras o usa una acción rápida
              </p>
              <div style={styles.quickActionsWrap}>
                {quickActions.map((qa) => (
                  <button
                    key={qa.action}
                    type="button"
                    onClick={() => handleQuickAction(qa.action)}
                    disabled={state.loading}
                    style={styles.quickChip(state.loading)}
                  >
                    <span>{qa.icon}</span>
                    <span>{qa.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {state.messages.map((msg, i) => (
            <div key={i} style={styles.bubble(msg.role === 'user')}>
              <div style={{ maxWidth: '85%' }}>
                <div style={styles.bubbleContent(msg.role === 'user')}>
                  {msg.role === 'assistant' ? renderMarkdown(msg.content) : msg.content}
                </div>
                {msg.role === 'assistant' && (
                  <div style={styles.feedbackRow}>
                    {/* Thumbs Up */}
                    <button
                      type="button"
                      title="Útil"
                      onClick={() => handleFeedback(i, 1)}
                      style={styles.feedbackBtn(feedbackMap.get(i) === 1, true, feedbackMap.has(i))}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill={feedbackMap.get(i) === 1 ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/>
                        <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
                      </svg>
                    </button>
                    {/* Thumbs Down */}
                    <button
                      type="button"
                      title="No útil"
                      onClick={() => handleFeedback(i, -1)}
                      style={styles.feedbackBtn(feedbackMap.get(i) === -1, false, feedbackMap.has(i))}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill={feedbackMap.get(i) === -1 ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z"/>
                        <path d="M17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/>
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Streaming: typewriter reveal of assistant response */}
          {state.loading && typewriterText ? (
            <div style={styles.bubble(false)}>
              <div style={styles.bubbleContent(false)}>
                {renderMarkdown(typewriterText)}
                <span style={{ opacity: 0.4 }}>▍</span>
              </div>
            </div>
          ) : state.loading && state.serverStatus === 'thinking' ? (
            <div style={styles.bubble(false)}>
              <div style={{
                ...styles.bubbleContent(false),
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                color: '#6B7280',
                fontStyle: 'italic',
              }}>
                <span style={{ animation: 'aicopilot-bounce 1s infinite', display: 'inline-block' }}>✨</span>
                <span>Analizando datos del CRM...</span>
              </div>
            </div>
          ) : state.loading && (
            <div style={styles.loadingDots}>
              <div style={styles.loadingBubble}>
                <span style={styles.dot(0)} />
                <span style={styles.dot(150)} />
                <span style={styles.dot(300)} />
              </div>
            </div>
          )}

          {state.error && (
            <div style={styles.error}>{state.error}</div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Quick actions bar (when there are messages) */}
        {hasMessages && (
          <div style={styles.quickBarWrap}>
            <div style={styles.quickBarInner}>
              {quickActions.slice(0, 3).map((qa) => (
                <button
                  key={qa.action}
                  type="button"
                  onClick={() => handleQuickAction(qa.action)}
                  disabled={state.loading}
                  style={styles.quickChip(state.loading)}
                >
                  <span>{qa.icon}</span>
                  <span>{qa.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input area */}
        <div style={styles.inputArea}>
          <div style={styles.inputRow}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Escribe tu pregunta..."
              rows={1}
              disabled={state.loading}
              style={{
                ...styles.textarea,
                opacity: state.loading ? 0.5 : 1,
              }}
              onInput={(e) => {
                const el = e.currentTarget;
                el.style.height = 'auto';
                el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
              }}
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={!input.trim() || state.loading}
              style={styles.sendBtn(!input.trim() || state.loading)}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M1.5 1.5L14.5 8L1.5 14.5V9.5L10.5 8L1.5 6.5V1.5Z" />
              </svg>
            </button>
          </div>
          <p style={styles.disclaimer}>
            AI puede cometer errores. Verifica la información importante.
          </p>
        </div>
      </div>
    </>
  );
}
